import { Box } from '@mui/system';
import { useCallback, type MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeleteChannelGuide } from '~/api/channel-guides/delete';
import { Tooltip } from '~/components/Tooltip';
import { DeleteButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { useConfirmDialog } from '~/hooks/dialogs';
import type { ChannelGuideSettings__ChannelGuide as ChannelGuide } from '../../queries/settings.generated';

export interface ActionsProps {
  channelGuide: ChannelGuide;
  onShowMenu?: MouseEventHandler<HTMLButtonElement>;
}

export const Actions = ({ channelGuide }: ActionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [confirm, confirmationProps] = useConfirmDialog();
  const [deleteChannelGuide] = useDeleteChannelGuide();

  const handleDelete = useCallback(async () => {
    if (!(await confirm())) return;
    await deleteChannelGuide({
      variables: { input: { channelGuideIds: [channelGuide.id] } },
    });
    navigate('../..');
  }, [channelGuide, confirm, deleteChannelGuide, navigate]);

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Tooltip title={channelGuide.canDestroy.reasons?.fullMessages.join(', ')}>
          <span>
            <DeleteButton disabled={!channelGuide.canDestroy.value} onClick={handleDelete} />
          </span>
        </Tooltip>
      </Box>
      <ConfirmDialog
        {...confirmationProps}
        cancel={t('common.actions.cancel')}
        confirm={t('common.dialogs.permanently_delete')}
        deleteConfirm
        prompt={t('pages.channel_guides.dialogs.delete.prompt')}
        title={t('pages.channel_guides.dialogs.delete.title')}
      />
    </>
  );
};
